import { useState } from 'react'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'
import { getAllRolesInBoolean } from 'src/mentor/layout/utils'
import { markBookedSessionAsCompletedFunction, sleep } from 'src/mentor/helpers/utilityFunctions'
import { downloadPDFFromURL, generatePrescriptionPDF } from 'src/mentor/components/PrescriptionMakingPage/prescriptionHelperFunctions'
import { mediaURL } from 'src/consumer/services'

export const usePrescriptionDownload = ({
  bookedSlot,
  sendPrescriptionPdfApiSignature,
  refreshBookedSlots
}) => {
  const [isDownloading, setIsDownloading] = useState(false)

  const generateFilename = (bookedSlot) => {
    return `prescription_${bookedSlot?.patientInfo?.patientNumber}_${dayjs().format('YYYY-MM-DD HH:mm:ss')}.pdf`
  }

  const downloadPDF = async ({ sendImmediately = false } = {}) => {
    if (isDownloading) return

    setIsDownloading(true)
    const toastId = toast.loading('Generating Prescription PDF...')

    try {
      await sleep(800)
      
      const data = await generatePrescriptionPDF({
        bookedEventId: bookedSlot?.id,
        sendImmediately,
        sendPrescriptionPdfApiSignature,
      })

      if (!data?.pdfMediaId) {
        throw new Error('No PDF media ID received')
      }

      const pdfUrl = `${mediaURL}${data.pdfMediaId}`
      const filename = generateFilename(bookedSlot)

      // Mark session as completed if sending to patient and user is doctor
      if (sendImmediately && !bookedSlot?.markedCompleted && getAllRolesInBoolean().isDoctor) {
        markBookedSessionAsCompletedFunction(bookedSlot, refreshBookedSlots, true)
      }

      await downloadPDFFromURL(pdfUrl, filename)
      return { success: true }

    } catch (error) {
      console.error('Error downloading prescription:', error)
      toast.error('Failed to generate/download prescription PDF')
      return { success: false, error }
    } finally {
      toast.dismiss(toastId)
      setIsDownloading(false)
    }
  }

  const downloadOnly = () => downloadPDF({ sendImmediately: false })
  const sendToPatient = () => downloadPDF({ sendImmediately: true })

  return {
    isDownloading,
    downloadOnly,
    sendToPatient,
    downloadPDF
  }
}